import { useEffect, useRef } from "react";
import { useParams } from "react-router";
import { useLastActiveContext } from "src/common/utils/hooks/useLastActiveContext.js";
import {
	ALL_LEGAL_ENTITIES,
	type LastActiveContext,
} from "src/common/utils/lastActiveContext.js";

/** The scope a URL implies, or undefined when it says nothing new to remember. */
const rememberedScopeForUrl = ({
	companyDomainId,
	legalEntityId,
	remembered,
}: {
	companyDomainId: string;
	legalEntityId: string | undefined;
	remembered: LastActiveContext | undefined;
}): LastActiveContext | undefined => {
	const staysInRememberedDomain =
		remembered?.companyDomainId === companyDomainId;
	// A domain-only page is not the same act as picking "Alla bolag".
	const keepsRememberedCompany = !legalEntityId && staysInRememberedDomain;
	if (keepsRememberedCompany) return undefined;

	const scope = {
		companyDomainId,
		legalEntityId: legalEntityId ?? ALL_LEGAL_ENTITIES,
	};
	const isAlreadyRemembered =
		staysInRememberedDomain &&
		remembered?.legalEntityId === scope.legalEntityId;
	return isAlreadyRemembered ? undefined : scope;
};

/** Keeps the remembered scope in step with the URL, so the sidebar pickers and
 * the landing redirect agree with where the user actually is — including
 * arrivals no picker saw: the Företag list, a deep link, or the back button. */
export const useSyncScopeFromUrl = () => {
	const { companyDomainId, legalEntityId } = useParams();
	const { value: remembered, write } = useLastActiveContext();
	// WHY: only a URL change is worth syncing; reacting to the remembered value would undo a profile Reset.
	const latest = useRef({ remembered, write });
	latest.current = { remembered, write };

	useEffect(() => {
		if (!companyDomainId) return;

		const scope = rememberedScopeForUrl({
			companyDomainId,
			legalEntityId,
			remembered: latest.current.remembered,
		});
		if (scope) latest.current.write(scope);
	}, [companyDomainId, legalEntityId]);
};
